import React from "react";
import { Link, useLocation } from "react-router-dom";
import ReactDOM from 'react-dom';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { getDatabase, ref, child, get } from 'firebase/database';
import Chad from "./images/chad.png";
import "./Header.css";
import { HeaderProps, Member } from "../types";

const Header: React.FC<HeaderProps> = (props) => {
    const location = useLocation();
    const path = location.pathname;
    const isDocsPage = path.startsWith("/docs/");
    const isSandboxPage = path.startsWith("/box/") || path.startsWith("/interviews/");
    const [isLoggedIn, setIsLoggedIn] = React.useState<boolean>(false);
    const [userAccountType, setUserAccountType] = React.useState<string | null>(null);
    const [creditsBalance, setCreditsBalance] = React.useState<number | null>(null);
    const [isLoadingAccountType, setIsLoadingAccountType] = React.useState<boolean>(true);
    const [hoveredMember, setHoveredMember] = React.useState<string | null>(null);
    const [tooltipStyles, setTooltipStyles] = React.useState<{ top: number, left: number } | null>(null);

    const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>, memberId: string) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setTooltipStyles({
            top: rect.bottom + 8, // 8px Offset below the bubble
            left: rect.left + (rect.width / 2)
        });
        setHoveredMember(memberId);
    };

    const handleMouseLeave = () => {
        setHoveredMember(null);
        setTooltipStyles(null);
    };

    React.useEffect(() => {
        const auth = getAuth();
        const unsubscribe = onAuthStateChanged(auth, async (user: User | null) => {
            setIsLoggedIn(!!user);
            if (user) {
                try {
                    const dbRef = ref(getDatabase());
                    const snapshot = await get(child(dbRef, `users/${user.uid}`));
                    if (snapshot.exists()) {
                        const userData = snapshot.val();
                        setUserAccountType(userData.accountType);
                        if (userData.accountType === 'company') {
                            const base = (process.env.NODE_ENV === 'production' &&
                                (!process.env.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL.includes('localhost')))
                                ? 'https://interview-sandbox-production.up.railway.app'
                                : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000');
                            try {
                                const token = await user.getIdToken();
                                const res = await fetch(`${base}/api/credits/balance`, {
                                    headers: { Authorization: `Bearer ${token}` },
                                });
                                if (res.ok) {
                                    const data = await res.json();
                                    setCreditsBalance(data.balance);
                                }
                            } catch {
                                // Ignore credits fetch errors
                            }
                        }
                    }
                } catch (error) {
                    console.error('Error getting user account type:', error);
                }
                setIsLoadingAccountType(false);
            } else {
                setUserAccountType(null);
                setCreditsBalance(null);
                setIsLoadingAccountType(false);
            }
        });
        return unsubscribe;
    }, []);

    return (
        <header style={props.style} className={props.darkMode === false ? "App-header" : "App-header-dark"}>
            <img className={props.darkMode === false ? "mainLogo" : "mainLogo-dark"} src={Chad} alt="Chad" />
            <Link className={props.darkMode === false ? "App-title neonText" : "App-title-dark neonText-dark"} to="/">
                Interview Sandbox
            </Link>

            {/* Right side content container */}
            <div className="header-right-content">
                {/* Presence Indicators */}
                {props.members && props.members.length > 0 && (
                    <div className="presence-container">
                        {props.members.map((member) => (
                            <div
                                key={member.id}
                                style={{ position: 'relative' }}
                                onMouseEnter={(e) => handleMouseEnter(e, member.id)}
                                onMouseLeave={handleMouseLeave}
                            >
                                <div
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '50%',
                                        backgroundColor: member.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#fff',
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        border: member.id === props.yourID ? '2px solid #fff' : 'none',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                        cursor: 'default',
                                        transition: 'transform 0.2s ease',
                                        transform: hoveredMember === member.id ? 'scale(1.1)' : 'scale(1)',
                                        overflow: 'hidden'
                                    }}
                                >
                                    {member.photoURL ? (
                                        <img
                                            src={member.photoURL}
                                            alt=""
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover'
                                            }}
                                        />
                                    ) : (
                                        member.name.charAt(0).toUpperCase()
                                    )}
                                </div>
                                {/* Portal Tooltip */}
                                {hoveredMember === member.id && tooltipStyles && ReactDOM.createPortal(
                                    <div style={{
                                        position: 'fixed',
                                        top: tooltipStyles.top,
                                        left: tooltipStyles.left,
                                        transform: 'translateX(-50%)',
                                        backgroundColor: props.darkMode ? '#1a1a1a' : '#2d2d2d',
                                        color: '#fff',
                                        padding: '8px 12px',
                                        borderRadius: '6px',
                                        fontSize: '13px',
                                        whiteSpace: 'nowrap',
                                        zIndex: 10000,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        pointerEvents: 'none'
                                    }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: member.displayName ? '2px' : '0' }}>
                                            {member.name} {member.id === props.yourID && <span style={{ opacity: 0.7 }}>(You)</span>}
                                        </div>
                                        {(props.memberActiveTabs?.[member.id] ?? (member.id === props.yourID ? props.activeTab : undefined)) && (
                                            <div style={{ fontSize: '11px', opacity: 0.8, marginBottom: member.displayName ? '2px' : 0 }}>
                                                Viewing: {(props.memberActiveTabs?.[member.id] ?? props.activeTab) === 'code' ? 'Code' : 'Diagram'}
                                            </div>
                                        )}
                                        {member.displayName && (
                                            <div style={{ fontSize: '11px', opacity: 0.8 }}>
                                                {member.displayName}
                                            </div>
                                        )}
                                        {/* Tooltip arrow - pointing up */}
                                        <div style={{
                                            position: 'absolute',
                                            top: '-6px',
                                            left: '50%',
                                            transform: 'translateX(-50%)',
                                            width: '0',
                                            height: '0',
                                            borderLeft: '6px solid transparent',
                                            borderRight: '6px solid transparent',
                                            borderBottom: `6px solid ${props.darkMode ? '#1a1a1a' : '#2d2d2d'}`
                                        }} />
                                    </div>,
                                    document.body
                                )}
                            </div>
                        ))}
                    </div>
                )}


                <div className="header-docs-extras">
                    {userAccountType === 'company' && creditsBalance !== null && !isSandboxPage && !isDocsPage && (
                        <Link
                            to="/dashboard/credits"
                            className={props.darkMode === false ? "header-credits-badge" : "header-credits-badge header-credits-badge-dark"}
                            title="Credits"
                        >
                            Credits: {creditsBalance}
                        </Link>
                    )}
                    {!isDocsPage && !isSandboxPage && (
                        <Link to="/docs/cli" className={props.darkMode === false ? "header-docs-link" : "header-docs-link header-docs-link-dark"}>
                            Docs
                        </Link>
                    )}
                    {props.extras && <div className="extras">{props.extras}</div>}
                </div>
            </div>
        </header>
    );
};

export default Header;
